import React from 'react';
import './FoxMascot.css';

/**
 * Standing fox mascot drawn in SVG, modelled on the landing hero fox
 * (assets/fox-money.png): chubby orange fox with a big head, cream muzzle and
 * belly, happy closed eyes, red scarf, holding a gold rupee coin with both paws.
 * Vector, so it stays sharp at any size.
 *
 * Each animated part has its own class (see FoxMascot.css); positioning and
 * entrance/exit motion are left to the parent via `className`.
 */
function FoxMascot({ className = '' }) {
  return (
    <svg
      className={`fox-mascot ${className}`.trim()}
      viewBox="0 0 300 330"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="foxm-fur" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fb9d3c" />
          <stop offset="1" stopColor="#ec7020" />
        </linearGradient>
        <linearGradient id="foxm-fur-dark" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f0802a" />
          <stop offset="1" stopColor="#d9631b" />
        </linearGradient>
        <linearGradient id="foxm-cream" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fffbf2" />
          <stop offset="1" stopColor="#ffe8c8" />
        </linearGradient>
        <radialGradient id="foxm-coin" cx="0.38" cy="0.32" r="0.75">
          <stop offset="0" stopColor="#fff3b8" />
          <stop offset="0.45" stopColor="#fcd34d" />
          <stop offset="1" stopColor="#f59e0b" />
        </radialGradient>
        <clipPath id="foxm-coin-clip">
          <circle cx="178" cy="250" r="33" />
        </clipPath>
      </defs>

      <ellipse className="fox-shadow" cx="150" cy="318" rx="104" ry="10" />

      {/* Big bushy tail (behind the body) */}
      <g className="fox-tail">
        <path
          d="M120 284 C 60 302, 4 272, 8 212 C 12 156, 56 122, 94 138 C 74 168, 76 228, 130 252 Z"
          fill="url(#foxm-fur)"
          stroke="#d4601a"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path
          d="M94 138 C 66 124, 28 136, 14 172 C 30 162, 50 164, 62 180 C 64 162, 76 146, 94 138 Z"
          fill="url(#foxm-cream)"
        />
      </g>

      {/* Chubby torso with belly and scarf */}
      <g className="fox-torso">
        <path
          d="M150 182 C 210 182, 228 236, 224 270 C 220 302, 188 316, 150 316 C 112 316, 80 302, 76 270 C 72 236, 90 182, 150 182 Z"
          fill="url(#foxm-fur)"
          stroke="#d4601a"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path
          d="M150 212 C 182 212, 192 246, 190 272 C 188 298, 172 308, 150 308 C 128 308, 112 298, 110 272 C 108 246, 118 212, 150 212 Z"
          fill="url(#foxm-cream)"
        />
        <path d="M94 188 C 126 212, 174 212, 206 188 L 210 205 C 176 230, 124 230, 90 205 Z" fill="#e5432f" />
        <path d="M112 214 L 92 246 L 110 250 L 126 220 Z" fill="#c7321f" />
      </g>

      {/* Stubby feet */}
      <ellipse cx="116" cy="314" rx="27" ry="13" fill="#5a2e1b" />
      <ellipse cx="184" cy="314" rx="27" ry="13" fill="#5a2e1b" />
      <ellipse cx="110" cy="310" rx="8" ry="3.5" fill="#7a4029" />
      <ellipse cx="178" cy="310" rx="8" ry="3.5" fill="#7a4029" />

      {/* Head */}
      <g className="fox-head">
        <g className="fox-ear fox-ear-left">
          <path
            d="M94 94 L 74 14 C 73 5, 82 2, 87 8 L 142 60 Z"
            fill="url(#foxm-fur-dark)"
            stroke="#d4601a"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path d="M99 82 L 85 28 L 128 64 Z" fill="#ffe2c4" />
        </g>
        <g className="fox-ear fox-ear-right">
          <path
            d="M206 94 L 226 14 C 227 5, 218 2, 213 8 L 158 60 Z"
            fill="url(#foxm-fur-dark)"
            stroke="#d4601a"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path d="M201 82 L 215 28 L 172 64 Z" fill="#ffe2c4" />
        </g>

        <path
          d="M150 44 C 206 44, 234 80, 236 112 L 260 128 L 236 139 C 226 178, 192 198, 150 198 C 108 198, 74 178, 64 139 L 40 128 L 64 112 C 66 80, 94 44, 150 44 Z"
          fill="url(#foxm-fur)"
          stroke="#d4601a"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path
          d="M62 118 C 88 108, 122 120, 150 146 C 178 120, 212 108, 238 118 L 258 129 L 236 140 C 226 178, 192 198, 150 198 C 108 198, 74 178, 64 140 L 42 129 Z"
          fill="url(#foxm-cream)"
        />

        <path d="M104 124 Q 119 104 134 124" stroke="#3a1e12" strokeWidth="6" strokeLinecap="round" fill="none" />
        <path d="M166 124 Q 181 104 196 124" stroke="#3a1e12" strokeWidth="6" strokeLinecap="round" fill="none" />

        <ellipse cx="102" cy="150" rx="15" ry="8.5" fill="#fb923c" opacity="0.5" />
        <ellipse cx="198" cy="150" rx="15" ry="8.5" fill="#fb923c" opacity="0.5" />

        <path d="M133 160 Q 150 190 167 160 Q 150 167 133 160 Z" fill="#7c2d12" />
        <ellipse cx="150" cy="175" rx="8" ry="5.5" fill="#fb7185" />
        <ellipse cx="150" cy="149" rx="12.5" ry="9" fill="#3a1e12" />
        <ellipse cx="145.5" cy="145.5" rx="4" ry="2.4" fill="#ffffff" opacity="0.75" />
      </g>

      {/* Arms holding the coin in front of the belly */}
      <g className="fox-coin-hold">
        <path d="M104 206 C 112 236, 128 252, 146 258" stroke="url(#foxm-fur-dark)" strokeWidth="24" strokeLinecap="round" fill="none" />
        <path d="M200 206 C 212 228, 214 250, 208 264" stroke="url(#foxm-fur-dark)" strokeWidth="24" strokeLinecap="round" fill="none" />
        <circle cx="178" cy="250" r="33" fill="url(#foxm-coin)" stroke="#e8900c" strokeWidth="3" />
        <circle cx="178" cy="250" r="24" fill="none" stroke="#f59e0b" strokeWidth="2.5" />
        <text x="178" y="262" textAnchor="middle" fontSize="33" fontWeight="800" fill="#d97706">₹</text>
        <g clipPath="url(#foxm-coin-clip)">
          <rect className="fox-coin-shine" x="150" y="206" width="13" height="90" fill="#ffffff" opacity="0.55" />
        </g>
        <ellipse cx="150" cy="260" rx="14" ry="12" fill="#5a2e1b" />
        <ellipse cx="208" cy="264" rx="14" ry="12" fill="#5a2e1b" />
      </g>

      {/* Twinkling sparkles */}
      <path className="fox-sparkle fox-sparkle-a" d="M262 60 L 266 72 L 278 76 L 266 80 L 262 92 L 258 80 L 246 76 L 258 72 Z" fill="#fcd34d" />
      <path className="fox-sparkle fox-sparkle-b" d="M236 196 L 239 204 L 247 207 L 239 210 L 236 218 L 233 210 L 225 207 L 233 204 Z" fill="#fbbf24" />
    </svg>
  );
}

export default FoxMascot;
